import { useEffect, useState } from "react";
import { Plus, ChevronRight, Sparkles, Zap, CheckCircle, Settings, Database } from "lucide-react";
import Apis from "@/components/architecture/Apis.tsx";
import Dependencies from "@/components/architecture/Dependencies.tsx";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import { apiGeneratePlan, apiGenerateShredPrompt, apiGetShredPrompt } from "@/services/ShredService";
import { ShredResponse, PlanRequest, Entry } from "@/@types/shred.ts";
import draftToHtml from "draftjs-to-html";
import { Formik, Form } from "formik";
//import CKEditorReadonly from "@/components/CKEditorReadonly.tsx";
import Modal from "@/components/ui/Modal";

interface StepThreeProps {
  onStepValidationResponse: (response: ShredResponse) => void;
  onStepError: (error: string) => void;
  shredId: string;
  shredName: string;
  shredDescription: string;
}

type GeneratePlanFormSchema = {
  shredName: string;
};

interface Api {
  isApi: boolean;
  input: {
    body: object | string;
    description: string;
  };
  output: {
    status: string;
    body: object | string;
    description: string;
  }[];
}

interface Externals {
  description: EditorState;
  type: string;
}

const StepThree = (props: StepThreeProps) => {
  const {
    shredId,
    shredName,
    shredDescription,
    onStepValidationResponse,
    onStepError
  } = props;
  
  const [dependency, setDependency] = useState<Externals[]>([]);
  const [apis, setApis] = useState<Api[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showNoIOModal, setShowNoIOModal] = useState(false);
  const [formValues, setFormValues] = useState<GeneratePlanFormSchema | null>(null);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  
  const onGetShredPrompt = async () => {
    try {
      setLoading(true);
      onStepError("");
      
      // First, call the generate entries endpoint
      await apiGenerateShredPrompt(shredId);
      
      // Start polling for results
      startPolling();
    } catch (errors: any) {
      setLoading(false);
      if ("response" in errors && "data" in errors.response) {
        if ("body" in errors.response.data) {
          onStepError(errors.response.data.body.message);
        } else {
          onStepError(errors.response.data.message);
        }
      } else {
        onStepError("An error occurred while generating the prompt");
      }
    }
  };
  
  const startPolling = () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await apiGetShredPrompt(shredId);
        
        if (response.data && response.data.state === "ENTRIES_GENERATED") {
          // Results are ready, process them
          const safeJsonParse = (str: string) => {
            if (!str) return "";
            
            if ((str.trim().startsWith("{") && str.trim().endsWith("}")) ||
              (str.trim().startsWith("[") && str.trim().endsWith("]"))) {
              try {
                return JSON.parse(str);
              } catch (error) {
                console.warn(`Invalid JSON format: ${str}`, error);
                return str;
              }
            } else {
              return str;
            }
          };
          
          setApis(response.data.body.entry.map((entry) => ({
            isApi: entry.inputs[0].api,
            input: {
              body: safeJsonParse(entry.inputs[0].example),
              description: entry.inputs[0].description
            },
            output: entry.outputs.map((output) => ({
              status: output.httpStatus || "",
              body: safeJsonParse(output.example),
              description: output.description || ""
            }))
          })));
          
          setDependency(response.data.body.external.map((external) => ({
            type: external.type,
            description: EditorState.createWithContent(
              ContentState.createFromText(external.description)
            )
          })));
          
          // Stop polling and loading
          clearInterval(pollInterval);
          setLoading(false);
          setIntervalId(null);
        }
        // If state is not "ENTRIES_GENERATED", continue polling
      } catch (error) {
        // Continue polling on error (the generation might still be in progress)
        console.warn("Polling error, continuing...", error);
      }
    }, 2000); // Poll every 2 seconds
    
    setIntervalId(pollInterval);
  };
  
  const loadEntries = () => {
    setLoading(true);
    onStepError("");
    apiGetShredPrompt(shredId)
      .then((response) => {
        if (response.data && response.data.state === "ENTRIES_GENERATED") {
          const safeJsonParse = (str: string) => {
            if (!str) return "";
            if ((str.trim().startsWith("{") && str.trim().endsWith("}")) ||
              (str.trim().startsWith("[") && str.trim().endsWith("]"))) {
              try {
                return JSON.parse(str);
              } catch (error) {
                console.warn(`Invalid JSON format: ${str}`, error);
                return str;
              }
            } else {
              return str;
            }
          };
          setApis(response.data.body.entry.map((entry) => ({
            isApi: entry.inputs[0].api,
            input: {
              body: safeJsonParse(entry.inputs[0].example),
              description: entry.inputs[0].description
            },
            output: entry.outputs.map((output) => ({
              status: output.httpStatus || "",
              body: safeJsonParse(output.example),
              description: output.description || ""
            }))
          })));
          setDependency(response.data.body.external.map((external) => ({
            type: external.type,
            description: EditorState.createWithContent(
              ContentState.createFromText(external.description)
            )
          })));
        }
      })
      .catch((error) => {
        if ("response" in error && "data" in error.response) {
          if ("body" in error.response.data) {
            onStepError(error.response.data.body.message);
          } else {
            onStepError(error.response.data.message);
          }
        } else {
          onStepError("An error occurred while loading entries");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  const addApi = () => {
    setApis([
      ...apis,
      {
        isApi: false,
        input: { body: {}, description: "" },
        output: [{ status: "", body: {}, description: "" }]
      }
    ]);
  };
  
  const submit = (
    _values: GeneratePlanFormSchema,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    setSubmitting(true);
    onStepError("");
    const entry: Entry[] = [];
    apis.forEach((api) => {
      const hasInput = Object.keys(api.input.body).length > 0;
      const hasOutput = api.output.every((a) => a.body !== null && a.body !== undefined);
      if (hasInput && hasOutput) {
        entry.push({
          inputs: [
            {
              api: api.isApi,
              description: api.input.description,
              example: JSON.stringify(api.input.body)
            }
          ],
          outputs: api.output.map((out) => ({
            httpStatus: api.isApi ? out.status : undefined,
            description: out.description,
            example: JSON.stringify(out.body)
          }))
        });
      }
    });
    const external = dependency?.map((dep) => ({
      type: dep.type,
      description: draftToHtml(convertToRaw(dep.description.getCurrentContent()))
    }));
    if (entry.length === 0) {
      onStepError("Please fill all the input & output fields");
      setSubmitting(false);
    } else {
      apiGeneratePlan<ShredResponse, PlanRequest>(shredId, { entry, external })
        .then((result) => {
          onStepValidationResponse(result.data);
        })
        .catch((err) => {
          if ("response" in err && "data" in err.response) {
            if ("body" in err.response.data) {
              onStepError(err.response.data.body.message);
            } else {
              onStepError(err.response.data.message);
            }
          } else {
            onStepError("An error occurred while generating the plan");
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };
  
  const handleFormSubmit = (
    values: GeneratePlanFormSchema,
    setSubmitting: (isSubmitting: boolean) => void,
    isGeneratePlan: boolean
  ) => {
    if (isGeneratePlan && apis.length === 0) {
      setFormValues(values);
      setShowNoIOModal(true);
      setSubmitting(false);
    } else {
      submit(values, setSubmitting);
    }
  };
  
  const handleNoIOConfirm = () => {
    if (formValues) {
      submit(formValues, () => {
        setShowNoIOModal(false);
        setFormValues(null);
      });
    }
  };
  
  const handlePopupConfirm = () => {
    if (formValues) {
      submit(formValues, () => setShowPopup(false));
    }
  };
  
  useEffect(() => {
    loadEntries();
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);
  
  return (
    <div className="space-y-6">
      <Modal
        isOpen={showPopup}
        onClose={() => setShowPopup(false)}
        onConfirm={handlePopupConfirm}
        type="warning"
        title="Warning"
        message="No inputs/outputs have been generated. Are you sure you want to proceed?"
      />
      
      <Modal
        isOpen={showNoIOModal}
        onClose={() => {
          setShowNoIOModal(false);
          setFormValues(null);
        }}
        onConfirm={handleNoIOConfirm}
        type="warning"
        title="No Inputs/Outputs Generated"
        message="You didn't generate any inputs/outputs. Would you like to proceed without them?"
      />
      
      {/* Progress Navigation Block */}
      <div
        className="bg-gradient-to-r from-[#6F6AF8]/10 to-[#8B87FF]/10 border border-[#6F6AF8]/20 rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-0 lg:justify-between">
          {/* Left Side - Information */}
          <div className="flex-1 lg:pr-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#6F6AF8]/10 rounded-lg">
                <Settings className="w-4 h-4 md:w-5 md:h-5 text-[#6F6AF8]" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  Define Inputs & Outputs
                </h3>
                <p className="text-xs md:text-sm text-gray-600">Step 3 of 5</p>
              </div>
            </div>
            
            <div className="space-y-2 text-xs md:text-sm text-gray-700">
              <p className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="leading-tight">Use AI to generate inputs/outputs from your description</span>
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="leading-tight">Or manually create custom inputs and outputs as needed</span>
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="leading-tight">Define any external dependencies your shred requires</span>
              </p>
            </div>
          </div>
          
          {/* Right Side - AI Action Button */}
          <div className="flex-shrink-0">
            <Formik
              initialValues={{ shredName: shredName, shredDescription: shredDescription }}
              onSubmit={(values, { setSubmitting }) => {
                handleFormSubmit(values, setSubmitting, true);
              }}
            >
              {({ isSubmitting }) => (
                <Form>
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className={`
                      relative overflow-hidden group w-full lg:w-auto
                      px-4 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold text-sm md:text-lg
                      bg-gradient-to-r from-[#6F6AF8] via-[#8B87FF] to-[#A855F7]
                      hover:from-[#5A56E8] hover:via-[#7C78F5] hover:to-[#9333EA]
                      text-white shadow-2xl hover:shadow-3xl
                      transform hover:scale-105 active:scale-95
                      transition-all duration-300 ease-out
                      border-2 border-white/20
                      ${isSubmitting ? "opacity-90" : ""}
                    `}
                  >
                    {/* Animated background gradient */}
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-500"></div>
                    
                    {/* Sparkle effects */}
                    <div
                      className="absolute top-1 left-1 w-2 h-2 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping"></div>
                    <div
                      className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping animation-delay-200"></div>
                    <div
                      className="absolute top-1/2 left-1/4 w-1 h-1 bg-white/60 rounded-full opacity-0 group-hover:opacity-100 animate-ping animation-delay-400"></div>
                    
                    {/* Button content */}
                    <div className="relative flex items-center justify-center gap-2 md:gap-3">
                      {isSubmitting ? (
                        <>
                          {/* AI Loading Animation */}
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 md:w-5 md:h-5 relative">
                              <div className="absolute inset-0 border-2 border-white/30 rounded-full"></div>
                              <div
                                className="absolute inset-0 border-2 border-t-white border-r-white/70 border-b-white/40 border-l-white/10 rounded-full animate-spin"></div>
                            </div>
                            <Sparkles className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />
                          </div>
                          <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                            <span className="hidden md:inline">AI Generating Plan...</span>
                            <span className="md:hidden">Generating...</span>
                          </span>
                        </>
                      ) : (
                        loading ? (
                          <>
                            {/* AI Loading Animation */}
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 md:w-5 md:h-5 relative">
                                <div className="absolute inset-0 border-2 border-white/30 rounded-full"></div>
                                <div
                                  className="absolute inset-0 border-2 border-t-white border-r-white/70 border-b-white/40 border-l-white/10 rounded-full animate-spin"></div>
                              </div>
                              <Sparkles className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />
                            </div>
                            <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                            <span className="hidden md:inline">AI Generating Inputs/Outputs...</span>
                            <span className="md:hidden">Generating...</span>
                          </span>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-1">
                              <Sparkles
                                className="w-3 h-3 md:w-4 md:h-4 group-hover:animate-bounce animation-delay-100" />
                            </div>
                            <span className="bg-gradient-to-r from-white to-white/90 bg-clip-text">
                              <span className="hidden md:inline">Validate & Generate Plan</span>
                              <span className="md:hidden">Generate Plan</span>
                            </span>
                            <ChevronRight
                              className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform duration-200" />
                          </>
                        )
                      )}
                    </div>
                    
                    {/* Glow effect */}
                    <div
                      className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-r from-[#6F6AF8] to-[#A855F7] opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300"></div>
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
      
      {/* Reference Description Section */}
      {/*<div className="bg-blue-50/50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-800">Reference: Your Shred Description</h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Shred Name</label>
              <div className="border border-gray-200 p-3 rounded-lg bg-white text-gray-700 font-medium">
                {shredName}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Description</label>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <CKEditorReadonly content={shredDescription}/>
              </div>
            </div>
          </div>
        </div>*/}
      
      {/* Input/Output Definition Section - Dark Mode */}
      <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-lg">
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0 sm:justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-800 rounded-lg">
                <Database className="w-4 h-4 md:w-5 md:h-5 text-gray-300" />
              </div>
              <div>
                <h4 className="text-base md:text-lg font-semibold text-white">Define Inputs & Outputs</h4>
                <p className="text-xs md:text-sm text-gray-400">Create or modify the data structure for your shred</p>
              </div>
            </div>
            
            {/* AI Generate Button moved here */}
            <div className="flex items-center gap-4">
                <span className="text-xs text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
                  {apis.length} {apis.length === 1 ? "Entry" : "Entries"}
                </span>
              
              <button
                data-tour="generate-entries"
                onClick={onGetShredPrompt}
                disabled={loading}
                className="relative overflow-hidden group px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-semibold text-xs md:text-sm bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-200 border border-purple-500/30"
              >
                <div className="relative flex items-center gap-2">
                  {loading ? (
                    <>
                      <div className="w-3 h-3 md:w-4 md:h-4 relative">
                        <div className="absolute inset-0 border-2 border-white/30 rounded-full"></div>
                        <div
                          className="absolute inset-0 border-2 border-t-white border-r-white/70 border-b-white/40 border-l-white/10 rounded-full animate-spin"></div>
                      </div>
                      <span className="hidden sm:inline">Generating...</span>
                      <span className="sm:hidden">Gen...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-3 h-3 md:w-4 md:h-4" />
                      <span className="hidden sm:inline">Generate Inputs & Outputs</span>
                      <span className="sm:hidden">Generate I/O</span>
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
          
          {/* Content Area */}
          <div className="bg-gray-800 rounded-xl md:rounded-2xl border border-gray-700 p-4 md:p-6">
            {/* Add New Entry Section */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 mb-6">
              <div className="md:col-span-6">
                <div
                  className="border-2 border-dashed border-gray-600 rounded-xl flex flex-col justify-center items-center h-24 md:h-32 gap-2 md:gap-3 cursor-pointer hover:bg-gray-700/50 hover:border-gray-500 transition-all duration-200"
                  onClick={addApi}
                >
                  <div className="p-2 md:p-3 bg-gray-700 rounded-full group-hover:bg-gray-600 transition-colors">
                    <Plus className="w-5 h-5 md:w-6 md:h-6 text-gray-300" />
                  </div>
                  <p className="text-gray-300 text-center text-xs md:text-sm font-medium">Create New Input</p>
                </div>
              </div>
            </div>
            
            {/* Separator */}
            {apis.length > 0 && (
              <div className="border-t border-gray-700 my-6"></div>
            )}
            
            {/* APIs List */}
            <div className="space-y-4">
              <Apis apis={apis} setApis={(apis) => setApis(apis)} />
            </div>
            
            {/* Empty State */}
            {apis.length === 0 && (
              <div className="text-center py-6 md:py-8">
                <div
                  className="p-3 md:p-4 bg-gray-700 rounded-full w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 flex items-center justify-center">
                  <Database className="w-6 h-6 md:w-8 md:h-8 text-gray-400" />
                </div>
                <h5 className="text-gray-300 font-medium mb-2 text-sm md:text-base">No Inputs/Outputs Defined</h5>
                <p className="text-gray-500 text-xs md:text-sm px-4">Use AI generation or create entries manually to get
                  started</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Dependencies Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 md:p-6">
          <Dependencies dependency={dependency} setDependency={setDependency} />
        </div>
      </div>
    </div>
  );
};

export default StepThree;